/**
 * Structured debug logging helpers for the sibling-order filtering pipeline.
 * @module siblingOrderFilter/logging
 */
import { createLogger } from "@risalabs_frontend_org/logger";
import { SiblingOrder } from "../../../data-model/siblingOrdersModel";

/** Namespace-scoped logger for the sibling-order filtering pipeline. */
export const logger = createLogger(
  "siblingFilter",
  "filterSiblingOrdersBasedOnConditions.ts",
);

/**
 * Builds a short human-readable identity string for a sibling order, used in
 * debug logs to trace which order passed or was rejected at each step.
 *
 * @param order - The sibling order to describe
 * @returns A string of the form `order_id=… dos=… status=…`
 */
export const describeOrder = (order: SiblingOrder): string => {
  const id = order?.order_id ?? order?.order?.order_id ?? "unknown";
  const dos = order?.order?.payload?.date_of_service ?? "n/a";
  const status = order?.order?.status?.medical_order_status ?? "n/a";
  return `order_id=${id} dos=${dos} status=${status}`;
};

/**
 * Logs the orders that were dropped between an input and output list (by object
 * identity), each with the supplied rejection reason.
 *
 * @param step - Label identifying the pipeline step doing the rejecting
 * @param reason - Human-readable reason the orders were rejected
 * @param before - The list of orders before filtering
 * @param after - The list of orders that survived filtering
 */
export const logRejected = (
  step: string,
  reason: string,
  before: SiblingOrder[],
  after: SiblingOrder[],
): void => {
  before
    .filter((order) => !after.includes(order))
    .forEach((order) =>
      logger.debug(`${step} REJECTED (${reason}): ${describeOrder(order)}`),
    );
};

/**
 * Logs the orders that survived a filtering step, each with the supplied
 * success reason.
 *
 * @param step - Label identifying the pipeline step doing the filtering
 * @param reason - Human-readable reason the orders passed
 * @param after - The list of orders that survived filtering
 */
export const logPassed = (
  step: string,
  reason: string,
  after: SiblingOrder[],
): void => {
  after.forEach((order) =>
    logger.debug(`${step} PASSED (${reason}): ${describeOrder(order)}`),
  );
};
